import { Directive } from '@angular/core';
import { UntypedFormGroup, Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';

@Directive()
export abstract class EaSimulationScpiPackFormBuilder extends PrFormAbstract {
  options: any = {};

  generate(entity: any, options?) {
    this.options = options;
    let scpis: UntypedFormGroup[] = [];
    if (entity)
      for (let scpi of entity)
        scpis.push(this.formBuilder.group(this.createModel(scpi)));

    let group = this.formBuilder.group({
      repartitionType: 'PERCENT',
      scpis: this.formBuilder.array(scpis, Validators.nullValidator),
    });

    this.form = group;

    return group;
  }

  public createModel(scpi) {
    const mostRecentPrice = scpi?.priceHistory
      ? scpi.priceHistory.reduce((prev, current) => {
          return new Date(prev.date) > new Date(current.date) ? prev : current;
        })
      : null;
    let currentPartPrice = mostRecentPrice
      ? mostRecentPrice.value
      : scpi.partPrice;

    // Durée par défaut des versements programmés et du réinvestissement = durée du projet.
    // Côté UI on affiche en années (plus lisible) ; la conversion ×12 vers les mois attendus
    // par le backend est faite dans extract().
    const defaultDurationYears =
      this.options?.projectDuration != null
        ? this.options.projectDuration / 12
        : null;

    let model = {
      id: [scpi ? scpi.id : null],
      name: [scpi ? scpi.name : null],
      partPrice: [
        scpi && currentPartPrice
          ? currentPartPrice
          : Math.floor(
              (scpi.allocationPercentage * this.options.investedAmount) /
                scpi.shareCount,
            ),
      ],
      dismembermentKey: [scpi ? scpi.dismembermentKey : null],

      allocationPercentage: [scpi ? scpi.allocationPercentage : null],
      shareCount: [
        scpi && scpi.allocationPercentage && currentPartPrice
          ? Math.floor(
              (scpi.allocationPercentage * this.options.investedAmount) /
                currentPartPrice,
            )
          : scpi.shareCount || null,
      ],
      investedAmount: null,

      // Versement programmé + réinvestissement des dividendes par SCPI.
      // Versement programmé par SCPI : montant € / mois (MONTHLY implicite côté V1).
      programmedAmount: [scpi?.programmedAmount ?? null],
      // Durée des versements EN ANNÉES côté UI. Pré-remplie avec la durée du projet.
      // Convertie en mois dans extract() avant l'envoi backend.
      programmedDurationYears: [
        scpi?.programmedDurationMonths != null
          ? scpi.programmedDurationMonths / 12
          : defaultDurationYears,
      ],

      // Taux de réinvestissement par SCPI : saisi en pourcentage (0..100), converti en
      // 0..1 avant l'envoi au backend.
      dividendReinvestmentRate: [
        scpi?.dividendReinvestmentRate != null
          ? scpi.dividendReinvestmentRate * 100
          : null,
      ],
      // Durée du réinvestissement EN ANNÉES côté UI, par défaut durée du projet.
      dividendReinvestmentDurationYears: [
        scpi?.dividendReinvestmentDurationMonths != null
          ? scpi.dividendReinvestmentDurationMonths / 12
          : defaultDurationYears,
      ],

      // Flags d'éligibilité SCPI : viennent du DTO ProductScpiHistoryDto, exposés via
      // /products/scpi/history. Stockés dans le form pour désactiver les inputs et
      // nettoyer les valeurs dans extract() si la SCPI ne supporte pas l'option.
      scpiReinvestissementDividendes: [
        scpi?.scpiReinvestissementDividendes ?? false,
      ],
      scpiPlanEpargneImmobilier: [scpi?.scpiPlanEpargneImmobilier ?? false],
    };

    return model;
  }

  extract() {
    let dto: any = this.emptyIsNull(this.form.value.scpis);
    if (Array.isArray(dto)) {
      dto = dto.map(scpi => {
        const supportsProgrammed = !!scpi.scpiPlanEpargneImmobilier;
        const supportsReinvestment = !!scpi.scpiReinvestissementDividendes;

        // Conversion année → mois pour les durées (backend attend des mois).
        // Forcer null si la SCPI ne supporte pas l'option : on ne propage rien au backend
        // pour éviter qu'une saisie résiduelle ne soit appliquée à tort.
        const programmedAmount = supportsProgrammed ? scpi.programmedAmount : null;
        const programmedDurationMonths = supportsProgrammed
          ? this.yearsToMonths(scpi.programmedDurationYears)
          : null;
        const dividendReinvestmentDurationMonths = supportsReinvestment
          ? this.yearsToMonths(scpi.dividendReinvestmentDurationYears)
          : null;
        // Le taux de réinvestissement est saisi en pourcentage côté UI (0..100). Le backend
        // attend une valeur 0..1 (cf. ScpiAllocation.dividendReinvestmentRate).
        const dividendReinvestmentRate =
          supportsReinvestment &&
          scpi.dividendReinvestmentRate != null &&
          scpi.dividendReinvestmentRate !== ''
            ? +scpi.dividendReinvestmentRate / 100
            : null;

        // On retire les champs UI-only (durées en années + flags d'éligibilité) et on
        // remonte les valeurs en mois attendues par le DTO Java ScpiAllocation.
        const {
          programmedDurationYears,
          dividendReinvestmentDurationYears,
          scpiReinvestissementDividendes,
          scpiPlanEpargneImmobilier,
          ...rest
        } = scpi;
        return {
          ...rest,
          programmedAmount,
          programmedDurationMonths,
          dividendReinvestmentDurationMonths,
          dividendReinvestmentRate,
        };
      });
    }
    return dto;
  }

  private yearsToMonths(years: any): number | null {
    if (years === null || years === undefined || years === '') return null;
    const numeric = +years;
    if (isNaN(numeric)) return null;
    return Math.round(numeric * 12);
  }
}
